import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import get from 'lodash.get';
import { FlexBox, Typography, Button, Grid, MuiBox } from '@kis-technologies/kis-ui';
import { ActionOverlay } from 'components/ActionOverlay';
import { CartTable } from 'components/CartTable';
import { ExitCartOverlay } from 'components/ExitCartOverlay';
import { Footer } from 'components/Footer';
import { LoadingOverlay } from 'components/LoadingOverlay';
import { useActions } from 'hooks/useActions';
import { selectors as cartSelectors, actions as cartActions, Cart, Item } from 'services/cart';
import { actions as cityPassActions, selectors as cityPassSelectors, CityPassTicket } from 'services/cityPass';
import { selectors as configSelectors, Configuration, LanguageField } from 'services/configuration';
import { selectors as ticketingSelectors, actions as ticketingActions } from 'services/ticketing';
import { formatTitle, formatCurrency } from 'utils/stringFormat';
import { useStyles } from './styled';
import DonationOverlay from './components/DonationOverlay/DonationOverlay';

const CartView = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  // Action Creators and Selectors
  const {
    releaseCart,
    setWasAutoReserveTriggered,
    setTiersConfig,
    setEventsConfig,
    setGenresConfig,
    setMembershipProductConfig,
    resetEventDetails,
    resetUpsell,
    resetUpsellProducts,
    resetMembershipProduct,
    resetGiftVoucher,
    applyDonation,
    removeDonation,
    resetCartError,
    resetDonationError
  } = useActions({
    releaseCart: cartActions.releaseCart,
    setWasAutoReserveTriggered: cityPassActions.setWasAutoReserveTriggered,
    setTiersConfig: ticketingActions.setTiersConfig,
    setEventsConfig: ticketingActions.setEventsConfig,
    setGenresConfig: ticketingActions.setGenresConfig,
    setMembershipProductConfig: ticketingActions.setMembershipProductConfig,
    resetEventDetails: ticketingActions.resetEventDetails,
    resetUpsell: ticketingActions.resetUpsell,
    resetUpsellProducts: ticketingActions.resetUpsellProducts,
    resetMembershipProduct: ticketingActions.resetMembershipProduct,
    resetGiftVoucher: cartActions.resetGiftVoucher,
    applyDonation: cartActions.applyDonation,
    removeDonation: cartActions.removeDonation,
    resetCartError: cartActions.resetCartError,
    resetDonationError: cartActions.resetDonationError
  });
  const cart = useSelector(cartSelectors.selectCart) as Cart;
  const totalToCharge = useSelector(cartSelectors.selectCartTotalToCharge);
  const giftVoucher = useSelector(cartSelectors.selectGiftVoucher);
  const giftVoucherAmountToUse = useSelector(cartSelectors.selectGiftVoucherAmountToUse);
  const donationProductInCart = useSelector(cartSelectors.selectDonationProductInCart);
  const config = useSelector(configSelectors.selectConfiguration) as Configuration;
  const loading = useSelector(cartSelectors.selectLoading);
  const reserving = useSelector(cartSelectors.selectReserving);
  const cartError = useSelector(cartSelectors.selectError);
  const cityPassTickets: CityPassTicket[] = useSelector(cityPassSelectors.selectTickets);
  const donation = useSelector(cartSelectors.selectDonation);
  const ticketingPath = useSelector(ticketingSelectors.selectTicketingConfigPath);

  const [donationPrompt, setDonationPrompt] = useState(false);
  const [exitCartPrompt, setExitCartPrompt] = useState(false);
  const [giftVoucherPrompt, setGiftVoucherPrompt] = useState(false);
  const [isCartTableOverlayOpen, setIsCartTableOverlayOpen] = useState(false);
  const [showCityPassErrorOverlay, setShowCityPassErrorOverlay] = useState(false);
  const [uiLoading, setUILoading] = useState(true);
  const [usedCityPassTickets, setUsedCityPassTickets] = useState(0);

  const {
    configuration: { flowTemplate }
  } = config;
  const fontFamily = get(config, 'configuration.globalSettings.fonts.primary');
  const languageSettings: LanguageField[] = get(config, 'configuration.languageSettings', []);
  const cartItemLabel = get(config, `configuration.labels.cartItem.${i18n.language}`, t('cart.cartItemLabel'));
  const cartLabel = get(config, `configuration.labels.cart.${i18n.language}`, t('cart.cartLabel'));
  const discountLabel = get(config, `configuration.labels.discount.${i18n.language}`, t('cart.discount'));
  const taxesAndFeesLabel = get(config, `messaging.${i18n.language}.taxesAndFeesLabel.message`, t('cart.taxesAndFees'));
  const cartConfig: Configuration['ticketing']['cart'] = get(config, `${ticketingPath}.cart`, {});
  const cartEnabled = get(cartConfig, 'enabled', false);
  const addMoreTickets = get(cartConfig, 'addMoreTickets', false);
  const header = get(cartConfig, `messages.header.${i18n.language}`, t('commonText.yourCart', { cartLabel }));
  const subheader = get(cartConfig, `messages.subheader.${i18n.language}`, t('cart.reviewOrder'));
  const showGiftVoucher = get(cartConfig, 'giftVoucher.enabled', false);
  const applyVoucherButton = get(
    cartConfig,
    `giftVoucher.messages.buttons.${i18n.language}[0]`,
    t('cart.applyGiftVoucher')
  );
  const removeVoucherButton = get(
    cartConfig,
    `giftVoucher.messages.buttons.${i18n.language}[1]`,
    t('cart.removeGiftVoucher')
  );
  const showDonationOverlay = get(config, `${ticketingPath}.donations.overlay.enabled`);
  const promoEnabled = get(config, `${ticketingPath}.promoCode.cart.enabled`, false);
  const promoMessages = get(config, `${ticketingPath}.promoCode.cart.messages`);
  const promoButton = get(promoMessages, `buttons.${i18n.language}[0]`, t('promoCode.enterButton'));

  const previousPage = (location.state?.from as string) || '';
  const hasOverlay =
    giftVoucherPrompt ||
    exitCartPrompt ||
    donationPrompt ||
    Boolean(cartError) ||
    showCityPassErrorOverlay ||
    isCartTableOverlayOpen;

  const classes = useStyles({
    hasBottomButtons: Boolean(showGiftVoucher || addMoreTickets),
    fontFamily
  })();

  // On initial render
  React.useEffect(() => {
    // Skip past the cart if it's not enabled
    if (!cartEnabled) {
      // Determine if coming back to the cart from a later page in the flow
      const followingPages = ['/ticketing/cart/customer', '/ticketing/genres', '/ticketing/purchase/payment'];
      if (followingPages.includes(previousPage)) {
        setExitCartPrompt(true);
      } else {
        handleNextClick();
      }
    }

    // Remove the donation from the cart IF it was added from the overlay
    // But not if the cart is disabled, since the overlay is only shown once
    if (cartEnabled && donationProductInCart && donation.source === 'DonationOverlay') {
      const removeParams = get(donationProductInCart, 'items[0].removeParams');
      if (removeParams) removeDonation(removeParams);
    }

    setUILoading(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (flowTemplate && flowTemplate === 'fair') {
      navigate('/ticketing/purchase/payment');
    }
  }, [flowTemplate]);

  // Reset the donation error if the overlay is not displayed
  React.useEffect(() => {
    if (donation.error) {
      setUILoading(false);
      if (!donationPrompt) resetDonationError();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [donationPrompt, donation.error]);

  // on update of cart - close overlay and redirect to the previous page (TicketSelectionView)
  React.useEffect(() => {
    if (!cart) setTiersConfig({ selected: '' });

    // re-routes to the attract loop page if there is no cart or cart quantity
    if (flowTemplate !== 'fair' && (!cart || !cart.quantity)) {
      if (cart) releaseCart(cart.releaseParams);
      navigate('/ticketing/attract');
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cart]);

  const releaseCartAndStartOver = () => {
    // Release Tickets
    releaseCart(cart.releaseParams);
    navigate('/ticketing/attract');
  };

  const handleAddMoreTicket = () => {
    setGenresConfig({ page: 1, selected: '' });
    setEventsConfig({ page: 1, selected: '' });
    setTiersConfig({ selected: '' });
    resetEventDetails();
    resetUpsell();
    resetUpsellProducts();
    resetMembershipProduct();
    setMembershipProductConfig({ selected: '' });
    setWasAutoReserveTriggered(false);
    navigate('/ticketing/genres', { state: { from: location.pathname } });
  };

  // Returns False if the user scanned more CP Tickets than they've used
  const areAllCityPassTicketsUsed = (): boolean => {
    if (!cityPassTickets || !cityPassTickets.length) return true;

    // -- Get the qty of items in the cart that have isCityPass
    let itemsInCartForCityPass = 0;
    cart.products.forEach((product) => {
      const items = product.subProducts ? (product.subProducts[0].items as Item[]) : (product.items as Item[]);
      if (items.length) {
        itemsInCartForCityPass += items.filter((item) => item.isCityPass).length;
      }
    });
    setUsedCityPassTickets(itemsInCartForCityPass);

    return cityPassTickets.length <= itemsInCartForCityPass;
  };

  const handleNextClick = () => {
    const allCityPassTicketsUsed = areAllCityPassTicketsUsed();
    if (!allCityPassTicketsUsed) {
      setShowCityPassErrorOverlay(true);
    } else {
      if (showDonationOverlay && !donation.source) {
        setDonationPrompt(true);
      } else {
        navigate('/ticketing/cart/customer');
      }
    }
  };

  const handleDonationConfirm = (amount: number) => {
    setUILoading(true);
    applyDonation(amount);
  };

  const handleGiftVoucherClick = () => {
    if (giftVoucher && giftVoucher.processed) {
      setGiftVoucherPrompt(true);
    } else {
      navigate('/ticketing/cart/gift-voucher');
    }
  };

  const onExitCartConfirm = () => {
    if (cartEnabled) {
      setExitCartPrompt(false);
    } else {
      navigate(previousPage || '/ticketing/cart/customer');
    }
  };

  const onCityPassErrorConfirm = () => {
    if (cartEnabled) {
      setShowCityPassErrorOverlay(false);
    } else {
      handleAddMoreTicket();
    }
  };

  // **************
  // **  RENDER  **
  // **************

  return (
    <>
      {(loading || reserving || uiLoading) && <LoadingOverlay style={{ zIndex: '99' }} />}
      {!loading && !reserving && !uiLoading && cartEnabled && (
        <Grid className={classes.cartViewOuterContainer}>
          <Grid container direction="column" className={classes.cartInnerContainer}>
            <>
              <MuiBox className={classes.cartContentArea}>
                {/* Title */}
                <MuiBox className={classes.cartHeaderArea}>
                  <MuiBox className={classes.cartHeading}>
                    <Typography variant="h2" sx={{ lineHeight: 1.2 }}>
                      {header}
                    </Typography>
                    <Typography fontSize={24}>{subheader}</Typography>
                  </MuiBox>
                  {/* Gift voucher and promo code buttons */}
                  <MuiBox className={classes.cartViewButtons}>
                    {showGiftVoucher && (
                      <Button
                        onClick={handleGiftVoucherClick}
                        appearance="white"
                        customSize="x-large"
                        data-testid="gift-voucher-button"
                        className={classes.cartWrapTextButton}
                        sx={{ padding: '0px 40px' }}
                      >
                        {giftVoucher.processed ? removeVoucherButton : applyVoucherButton}
                      </Button>
                    )}
                    {promoEnabled && (
                      <Button
                        onClick={() => navigate('/ticketing/cart/promo')}
                        appearance="white"
                        customSize="x-large"
                        data-testid="promo-code-button"
                        className={classes.cartWrapTextButton}
                      >
                        {promoButton}
                      </Button>
                    )}
                  </MuiBox>
                </MuiBox>
                {/* Table of Reserved Tickets */}
                <CartTable hasRemoveColumn setIsOverlayOpen={setIsCartTableOverlayOpen} />
              </MuiBox>
              <MuiBox className={classes.cartBottomArea}>
                {/* Component will display only in portrait mode*/}
                <MuiBox className={classes.cartViewButtonsPortrait}>
                  {addMoreTickets ? (
                    <Button
                      onClick={handleAddMoreTicket}
                      appearance="white"
                      customSize="x-large"
                      startIconName="plus_circle"
                      data-testid="add-more-button"
                      className={classes.cartWrapTextButton}
                    >
                      {t('cart.addMore')} {cartItemLabel}
                    </Button>
                  ) : null}
                  {showGiftVoucher && (
                    <Button
                      onClick={handleGiftVoucherClick}
                      appearance="white"
                      customSize="x-large"
                      data-testid="gift-voucher-button"
                      className={classes.cartWrapTextButton}
                    >
                      {giftVoucher.processed ? removeVoucherButton : applyVoucherButton}
                    </Button>
                  )}
                  {promoEnabled && (
                    <Button
                      onClick={() => navigate('/ticketing/cart/promo')}
                      appearance="white"
                      customSize="x-large"
                      data-testid="promo-code-button"
                      className={classes.cartWrapTextButton}
                    >
                      {promoButton}
                    </Button>
                  )}
                </MuiBox>
                <MuiBox className={classes.cardAmountDivider} />
                {/* Taxes Fees Total */}
                <MuiBox className={classes.cartTotalAmountBlock}>
                  {cart && (
                    <FlexBox flexOverride="none" direction="row" fullWidth justify="between">
                      <FlexBox flexOverride="none" direction="row" justify="start" align="end">
                        <Typography fontSize={34} fontWeight={600} align="left">
                          {cart.quantity
                            ? `${formatTitle(cartItemLabel)}${t('commonText.colon')} ${cart.quantity}`
                            : null}
                        </Typography>
                      </FlexBox>
                      <FlexBox flexOverride="none" direction="row" justify="end" align="end">
                        <FlexBox direction="column" align="end">
                          {cart.feeTotal > 0 && (
                            <Typography fontSize={24} align="right">
                              {`${taxesAndFeesLabel}${t('commonText.colon')} `}
                            </Typography>
                          )}
                          {cart.subTotal && (giftVoucherAmountToUse || cart.discount) ? (
                            <>
                              <Typography fontSize={24} align="right">
                                {`${t('cart.subTotal')}${t('commonText.colon')}`}
                              </Typography>
                              {cart.discount ? (
                                <Typography fontSize={24} align="right">
                                  {`${discountLabel}${t('commonText.colon')} `}
                                </Typography>
                              ) : null}
                              {giftVoucherAmountToUse ? (
                                <Typography fontSize={24} align="right">
                                  {`${t('cart.giftVoucher')}${t('commonText.colon')} `}
                                </Typography>
                              ) : null}
                            </>
                          ) : null}
                          <Typography fontSize={34} fontWeight={600} align="right">
                            {`${t('commonText.total')}${t('commonText.colon')} `}
                          </Typography>
                        </FlexBox>
                        <FlexBox direction="column" align="end" margin="0 0 0 2">
                          {cart.feeTotal > 0 && (
                            <Typography fontSize={24} align="right">
                              {formatCurrency(cart.feeTotal, {}, languageSettings)}
                            </Typography>
                          )}
                          {cart.subTotal && (giftVoucherAmountToUse || cart.discount) ? (
                            <>
                              <Typography fontSize={24} align="right">
                                {formatCurrency(cart.subTotal, {}, languageSettings)}
                              </Typography>
                              {cart.discount ? (
                                <Typography fontSize={24} align="right">
                                  {formatCurrency(cart.discount, {}, languageSettings)}
                                </Typography>
                              ) : null}
                              {giftVoucherAmountToUse ? (
                                <Typography fontSize={24} align="right">
                                  -{formatCurrency(giftVoucherAmountToUse, {}, languageSettings)}
                                </Typography>
                              ) : null}
                            </>
                          ) : null}
                          <Typography fontSize={34} fontWeight={600} align="right">
                            {formatCurrency(totalToCharge, {}, languageSettings)}
                          </Typography>
                        </FlexBox>
                      </FlexBox>
                    </FlexBox>
                  )}
                </MuiBox>
              </MuiBox>
            </>
          </Grid>
        </Grid>
      )}
      {/* Footer */}
      {!loading && !reserving && !uiLoading && cartEnabled && (
        <Footer
          onPrevClick={() => setExitCartPrompt(true)}
          onNextClick={handleNextClick}
          prevText={t('commonText.startOver')}
          nextText={t('commonText.continue')}
          skipNavPrompt={hasOverlay}
        >
          {addMoreTickets ? (
            <MuiBox className={classes.cartViewButtons}>
              <Button
                onClick={handleAddMoreTicket}
                appearance="white"
                customSize="x-large"
                startIconName="plus_circle"
                width={400}
                data-testid="add-more-button"
              >
                {t('cart.addMore')} {cartItemLabel}
              </Button>
            </MuiBox>
          ) : null}
        </Footer>
      )}

      {/* Overlay to confirm exiting and releasing the cart */}
      {exitCartPrompt && <ExitCartOverlay onCancel={releaseCartAndStartOver} onConfirm={onExitCartConfirm} />}

      {/* Overlay to show an error from removing tickets */}
      {cartError && (
        <ActionOverlay
          title={t('cart.unableToRemove', { cartLabel })}
          confirmText={t('commonText.tryAgain')}
          onConfirm={resetCartError}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Overlay to show alert in CityPASS flow if all passes are not redeemed yet */}
      {showCityPassErrorOverlay && (
        <ActionOverlay
          title={t('cityPass.cpCheckoutPopup', { scanned: cityPassTickets.length, used: usedCityPassTickets })}
          confirmText={cartEnabled ? t('cityPass.backToCart', { cartLabel }) : t('commonText.ok')}
          onConfirm={onCityPassErrorConfirm}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Overlay to confirm removing a gift voucher from the cart */}
      {giftVoucherPrompt && (
        <ActionOverlay
          title={t('cart.removeVoucherTitle')}
          cancelText={t('commonText.cancel')}
          confirmText={t('cart.yesRemove')}
          onCancel={() => setGiftVoucherPrompt(false)}
          onConfirm={() => {
            resetGiftVoucher();
            setGiftVoucherPrompt(false);
          }}
          height={500}
          style={{ background: 'rgba(255,255,255,0.95)' }}
        />
      )}

      {/* Overlay to add a donation to the cart */}
      {donationPrompt && (
        <DonationOverlay onDeny={() => navigate('/ticketing/cart/customer')} onConfirm={handleDonationConfirm} />
      )}
    </>
  );
};

export default CartView;
